"use client";

import { type QueryKey, useMutation, useQueryClient } from "@tanstack/react-query";
import { getOnlineStatusSnapshot } from "@/lib/shared/offline/online-store";
import { createScoreIdempotencyKey, enqueueOfflineScoreMutation, executeOfflineScoreRequest, type OfflineScoreMutation, type OfflineScoreMutationRequest } from "@/lib/shared/offline/score-mutations";
export type OfflineScoreMutationResult<TData> = {
  queued: false;
  data: TData | null;
} | {
  queued: true;
  queueItem: OfflineScoreMutation;
};
export type UseOfflineScoreMutationOptions<TVariables, TData> = {
  buildRequest: (variables: TVariables) => OfflineScoreMutationRequest;
  onMutate?: (variables: TVariables) => Promise<void> | void;
  onSuccess?: (result: OfflineScoreMutationResult<TData>, variables: TVariables) => Promise<void> | void;
  onError?: (error: Error, variables: TVariables) => Promise<void> | void;
  onQueued?: (queueItem: OfflineScoreMutation, variables: TVariables) => Promise<void> | void;
  invalidateQueryKeys?: readonly QueryKey[];
};
function isOfflineMutationFallbackError(error: unknown) {
  if (!getOnlineStatusSnapshot()) {
    return true;
  }
  if (!(error instanceof Error)) {
    return false;
  }
  return /network|fetch|offline|connection/i.test(error.message);
}
export function useOfflineScoreMutation<TVariables, TData = unknown>(options: UseOfflineScoreMutationOptions<TVariables, TData>) {
  const queryClient = useQueryClient();
  return useMutation<OfflineScoreMutationResult<TData>, Error, TVariables>({
    mutationFn: async variables => {
      const request = options.buildRequest(variables);
      const normalizedRequest: OfflineScoreMutationRequest = {
        ...request,
        invalidateQueryKeys: request.invalidateQueryKeys ?? options.invalidateQueryKeys ?? []
      };
      const idempotencyKey = createScoreIdempotencyKey();
      if (!getOnlineStatusSnapshot()) {
        const queueItem = await enqueueOfflineScoreMutation(normalizedRequest, idempotencyKey);
        return {
          queued: true,
          queueItem
        };
      }
      try {
        const data = (await executeOfflineScoreRequest(normalizedRequest, idempotencyKey)) as TData | null;
        for (const queryKey of normalizedRequest.invalidateQueryKeys ?? []) {
          await queryClient.invalidateQueries({
            queryKey
          });
        }
        return {
          queued: false,
          data
        };
      } catch (error) {
        if (!isOfflineMutationFallbackError(error)) {
          throw error instanceof Error ? error : new Error("Failed to record score");
        }
        const queueItem_0 = await enqueueOfflineScoreMutation(normalizedRequest, idempotencyKey);
        return {
          queued: true,
          queueItem: queueItem_0
        };
      }
    },
    onMutate: async variables_0 => {
      await options.onMutate?.(variables_0);
    },
    onSuccess: async (result, variables_1) => {
      if (result.queued) {
        await options.onQueued?.(result.queueItem, variables_1);
      }
      await options.onSuccess?.(result, variables_1);
    },
    onError: async (error_0, variables_2) => {
      await options.onError?.(error_0, variables_2);
    }
  });
}